import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import PageHeader from "../../components/PageHeader";
import WidgetsList from "./WidgetsList";
import { UseCompanyColors } from "../../hooks/company";
import { DEFAULT_BRAND, brandRgba } from "../../helpers/brandColor";

const Widgets = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryColor: companyBrandColor } = UseCompanyColors();
  const brand = companyBrandColor || DEFAULT_BRAND;

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  return (
    <>
      <PageHeader
        headerText="Widgets"
        subheaderText="Manage Your Widgets for efficient tracking"
        onBack={() => navigate(-1)}
        inlineBack
      />

      <SoftBox sx={{ margin: { mini: "1rem", sm: "1.5rem" } }}>
        {/* Page Content */}
        <Grid
          container
          alignItems="center"
          columnSpacing={2}
          rowSpacing={{ mini: 0, lg: 2 }}
        >
          <Grid item mini={12} lg={7}>
            <SoftBox>
              <SoftTypography
                component="h4"
                sx={{ color: "#495057", fontWeight: "600", fontSize: "24px" }}
              >
                Widgets Studio
              </SoftTypography>
              <SoftTypography
                component="p"
                sx={{
                  fontSize: "14px",
                  color: "#667085",
                  fontWeight: "500",
                  marginBottom: { mini: "8px", lg: "20px" },
                }}
              >
                Easily activate any widgets of your choice to get better insights to your activities
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid item mini={12} lg={5}>
            <SoftBox
              sx={{
                position: "relative",
                width: "100%",
                display: "flex",
                alignItems: "center",
                height: isMobile ? "38px" : "46px",
                background: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                transition: "background-color 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
                "&:focus-within": {
                  borderColor: brand,
                  boxShadow: `0 0 0 4px ${brandRgba(brand, 0.14)}`,
                },
                "&:focus-within .widgets-search-icon": { color: brand },
              }}
            >
              <SoftBox
                className="widgets-search-icon"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  paddingLeft: isMobile ? "13px" : "17px",
                  paddingRight: "8px",
                  color: "#9AA4B2",
                  transition: "color 150ms ease",
                }}
              >
                <svg width={isMobile ? "16" : "18"} height={isMobile ? "16" : "18"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.0002 21L16.6572 16.657M16.6572 16.657C17.4001 15.9141 17.9894 15.0321 18.3914 14.0615C18.7935 13.0909 19.0004 12.0506 19.0004 11C19.0004 9.94936 18.7935 8.90905 18.3914 7.93842C17.9894 6.96779 17.4001 6.08585 16.6572 5.34296C15.9143 4.60007 15.0324 4.01078 14.0618 3.60874C13.0911 3.20669 12.0508 2.99976 11.0002 2.99976C9.9496 2.99976 8.90929 3.20669 7.93866 3.60874C6.96803 4.01078 6.08609 4.60007 5.34321 5.34296C3.84288 6.84329 3 8.87818 3 11C3 13.1217 3.84288 15.1566 5.34321 16.657C6.84354 18.1573 8.87842 19.0002 11.0002 19.0002C13.122 19.0002 15.1569 18.1573 16.6572 16.657Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SoftBox>
              <SoftBox
                component="input"
                type="text"
                placeholder="Search for widgets"
                value={searchTerm}
                onChange={handleSearch}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontFamily: "inherit",
                  fontSize: isMobile ? "13px" : "14.5px",
                  fontWeight: 500,
                  color: "#1D2939",
                  padding: 0,
                  "&::placeholder": { color: "#9AA4B2", opacity: 1, fontWeight: 400 },
                }}
              />
              {searchTerm && (
                <SoftBox
                  component="button"
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    width: "22px",
                    height: "22px",
                    marginRight: isMobile ? "8px" : "12px",
                    border: 0,
                    borderRadius: "50%",
                    background: "rgba(15,23,42,0.08)",
                    color: "#667085",
                    cursor: "pointer",
                    transition: "background-color 120ms ease, color 120ms ease",
                    "&:hover": { background: brandRgba(brand, 0.14), color: brand },
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </SoftBox>
              )}
            </SoftBox>
          </Grid>
        </Grid>

        <SoftBox
          sx={{
            backgroundColor: "#FFF",
            padding: "2rem",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <WidgetsList searchTerm={searchTerm} />
        </SoftBox>
      </SoftBox>
    </>
  );
};

export default Widgets;
